/**
 * Migration subpage that is used for `pages/migration.jsx`.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module components/subpages/migrate.jsx
 * @class SubPageMigration
 * @since 0.0.1
 */

import React, {Component} from 'react';
import Web3 from 'web3';
import energiExtension from '@energi/web3-ext';
import {Snackbar, SnackbarAction} from '@rmwc/snackbar';
import gen2MigrationModule from '../../lib/smart-contracts/gen2-migration';
import mockgen2MigrationModule from '../../lib/smart-contracts/mock-gen2-migration';
import {networkOptions} from '../../lib/constants/network-info';
import translations from '../../lib/translations';
import demoState from '../../globals/demo-usage';

import Title from '../Title';
import Footer from '../Footer';
import Step1 from './migration/steps1';
import Step2 from './migration/steps2';
import Step3 from './migration/steps3';

class Migrate extends Component {
    constructor(props) {
        super(props);
        const instance = this;
        instance.state = {
            claimedPrivatekey: null,
            privateKey: '',
            gen3Address: '',
            balance: '',
            gen2WalletType: 'wallet_dump',
            isDemo: false
        };
        instance.translate = translations.init(props);
        instance.handleChangePrivateKey = instance.handleChangePrivateKey.bind(instance);
        instance.handleChangeGen3Address = instance.handleChangeGen3Address.bind(instance);
        instance.handleChangeStep = instance.handleChangeStep.bind(instance);
        instance.handleChangeWalletDump = instance.handleChangeWalletDump.bind(instance);
        instance.handleChangeMnemonic = instance.handleChangeMnemonic.bind(instance);
        instance.handleMigrate = instance.handleMigrate.bind(instance);
        instance.handleChangeGen2Choice = instance.handleChangeGen2Choice.bind(instance);
        instance.resetState = instance.resetState.bind(instance);
        instance.isClaimedKey = instance.isClaimedKey.bind(instance);
        // initiate the gen2migration module:
        instance.web3 = new Web3(networkOptions.networkAddress);
        energiExtension.extend(instance.web3);
        instance.gen2Migration = instance.state.isDemo ? mockgen2MigrationModule(instance.web3, networkOptions) : gen2MigrationModule(instance.web3, networkOptions);
        instance.changeDemo = instance.changeDemo.bind(instance);
    }

    componentDidMount() {
        const data = demoState.$demo;
        data.watch(state => this.changeDemo(state));
    }

    componentDidUpdate() {
        this.gen2Migration = this.state.isDemo ? mockgen2MigrationModule(this.web3, networkOptions) : gen2MigrationModule(this.web3, networkOptions);
    }

    /**
   * resets the following state, balance, step, step1, step2,step3
   *
   * @method resetState
   * @param none
   * @return {Boolean}
   * @since 0.0.1
   */

    resetState() {
        try {
            this.setState({balance: '', privateKey: '', gen3Address: '', gen2WalletType: 'wallet_dump', isClaimed: false, isDemo: false});
            let props = this.props;
            props.setStep(1);
            props.completeStep1(false);
            props.completeStep2(false);
            props.completeStep3(false);
            return true;
        }
        catch (err) {
            return err;
        }
    }

    /**
   * Validates whether the user input has the right length for a Gen2 Private key.
   *
   * @method validPrivateKey
   * @param privateKey {String} Gen2 private key
   * @return {ReactComponent}
   * @since 0.0.1
   */
    validPrivateKey(privateKey) {
        return typeof privateKey === 'string' && privateKey.length === 52;
    }

    isClaimedKey(privateKey) {
        return this.state.claimedPrivatekey === privateKey;
    }

    /**
   * Retrieves the Gen2 balance that is available for migration.
   * This is the balance of only `not blacklisted` addresses at the time Gen2 is halted, minus any balance that already has been migrated.
   *
   * @method askBalance
   * @param config {Object} either the property `privateKey`, `walletKeys` or `mnemonic` should be defined. In case of mnemonic, there will also be an extraPhrase and account property
   * @param [config.privateKey] {String} Gen2 private key
   * @param [config.walletKeys] {Array} List of Gen2 private keys
   * @param [config.mnemonic] {String} Mnemonic Phrase
   * @param [config.extraPhrase] {String} extra phrase (password) for Mnemonic Phrase
   * @param [config.purpose] {Number} first number of the BIP44 specification: likely to be 44
   * @param [config.coinType] {Number} second number of the BIP44 specification: coin type
   * @param [config.account] {Number} third number op the BIP44 specification: account number
   * @return {ReactComponent}
   * @since 0.0.1
   */
    askBalance(config) {
        const instance = this,
            gen2WalletType = instance.state.gen2WalletType;

        if (config.privateKey || config.walletKeys || config.mnemonic) {
            instance.setState(
                {
                    loading: true,
                    balance: ''
                },
                async () => {
                    let balance, response;
                    try {
                        if (gen2WalletType==='wallet_dump') {
                            response = await instance.gen2Migration.getBalancedCoins(config.walletKeys);
                            instance.setState({
                                loading: false,
                                balance: response.totalAmount,
                                walletKeys: response.balancedKeys,
                            });
                        }
                        else if (gen2WalletType==='gen_2_key') {
                            balance = await instance.gen2Migration.getCoinBalance(config.privateKey);
                            instance.setState({
                                loading: false,
                                balance,
                            });
                        }
                        else if (gen2WalletType==='mnemonic') {
                            response = await instance.gen2Migration.getBalancedCoinsByMnemonic(config.mnemonic, config.extraPhrase, config.purpose, config.coinType, config.account, config.keyPoolSize);
                            instance.setState({
                                loading: false,
                                balance: response.totalAmount,
                                walletKeys: response.balancedKeys,
                            });
                        }
                    }
                    catch (err) {
                        instance.setState({
                            loading: false,
                            errorMessage: err.message,
                            balance: '',
                        });
                    }
                }
            );
        }
    }

    /**
   * Callback for `onChangePrivateKey`, will update the value of Gen2 Private key, when entered from the Input element,
   * Property is used inside the Component Step1.jsx.
   *
   * @method handleChangePrivateKey
   * @param e {Object} Input event object
   * @since 0.0.1
   */
    handleChangePrivateKey(e) {
        this.setState(
            {
                privateKey: e.target.value,
                walletKeys: [],
                balance: '',
            },
            () => {
                const privateKey = this.state.privateKey,
                    claimedKey = this.isClaimedKey(privateKey);

                if (claimedKey) {
                    this.setState({
                        balance: '0'
                    });
                }
                else if (this.validPrivateKey(privateKey)) {
                // we can ask for the balance
                    this.askBalance({privateKey});
                }
            }
        );
    }

    /**
   * Callback for `onChangeGen3Address`, will update the value of Gen3 Public key
   * Property is used inside the Component Step2.jsx.
   *
   * @method handleChangeGen3Address
   * @since 0.0.1
   */
    // handles address key step2 form
    handleChangeGen3Address(e) {
        this.setState({
            gen3Address: e.target.value
        });
    }

    /**
   * Callback for `onChangeMnemonic`, will update the value of `walletKeys` with a new list of Gen2 Private keys.
   *
   * @method handleChangeGen3Address
   * @since 0.0.1
   */
    handleChangeMnemonic(mnemonic, extraPhrase, purpose, coinType, account, keyPoolSize, askBalance) {
        this.setState(
            {
                balance: '',
                walletKeys: [],
            },
            () => {
                // we may ask for the balance
                askBalance && this.askBalance({
                    account,
                    coinType,
                    extraPhrase,
                    keyPoolSize,
                    mnemonic,
                    purpose,
                });
            }
        );
    }

    /**
   * Callback for `onChangeGen2Choice`, will update the value of `state.gen2WalletType`
   *
   * @method handleChangeGen2Choice
   * @param gen2WalletType {String} the new gen2WalletType
   * @since 0.4.0
   */
    handleChangeGen2Choice(gen2WalletType) {
        const instance = this,
            state = instance.state;

        let mnemonicData;
        if (gen2WalletType==='wallet_dump') {
            instance.setState({
                gen2WalletType,
                walletKeys: [],
                balance: '',
            });
        }
        else if (gen2WalletType==='gen_2_key') {
            instance.setState({
                gen2WalletType
            },
            () => {
                instance.handleChangePrivateKey({target: {value: state.privateKey}});
            });
        }
        else if (gen2WalletType==='mnemonic') {
            instance.setState({
                gen2WalletType,
                balance: '',
            },
            () => {
                mnemonicData = instance._step1.getMnemonicData();
                mnemonicData.isValid && instance.handleChangeMnemonic(mnemonicData.mnemonic, mnemonicData.extraPhrase, mnemonicData.account, true);
            });
        }
    }

    /**
   * Callback for `onChangeWalletDump`, will update the value of `walletKeys` with a new list of Gen2 Private keys.
   * Property is used inside the Component Step1.jsx.
   *
   * @method handleChangeWalletDump
   * @param keys {Array} list of Gen2 Private keys.
   * @since 0.0.1
   */
    handleChangeWalletDump(keys) {
        const walletKeys = keys
            .map(k => (this.validPrivateKey(k) ? k : null)) // make sure not to pass a ref to `keys`
            .filter(k => !!k); // only valid keys
        this.setState(
            {
                balance: '',
                walletKeys: [],
            },
            () => {
                // we can ask for the balance
                this.askBalance({walletKeys});
            }
        );
    }

    /**
   * Processes the full migration of Gen2 into Gen3, based upon all settings (state values), made accross the workflow.
   *
   * @method handleMigrate
   * @return {ReactComponent}
   * @since 0.0.1
   */
    handleMigrate() {
        const instance = this,
            state = instance.state,
            gen2WalletType = state.gen2WalletType,
            multipleKeys = state.walletKeys && state.walletKeys.length > 0;

        if (state.privateKey || multipleKeys) {
            instance.setState(
                {
                    loading: true,
                },
                async () => {
                    if (gen2WalletType==='gen_2_key') {
                        try {
                            await instance.gen2Migration.claim(state.privateKey, state.gen3Address);
                            instance.setState({
                                loading: false,
                                isClaimed: true,
                                claimedPrivatekey: state.privateKey
                            });
                            instance.props.completeStep3(true);
                            instance.handleChangeStep(0);
                        }
                        catch (err) {
                            instance.setState({
                                loading: false,
                                balance: '',
                                errorMessage: instance.translate.t('COINS_NOT_CLAIMED', true) + ': ' +err.message,
                            });
                        }
                    }
                    else if (((gen2WalletType==='wallet_dump') || (gen2WalletType==='mnemonic')) && state.walletKeys.length > 0) {
                        try {
                            await instance.gen2Migration.claimMultiple(state.walletKeys, state.gen3Address);
                            instance.setState({
                                loading: false,
                                isClaimed: true,
                            });
                            instance.props.completeStep3(true);
                            instance.handleChangeStep(0);
                        }
                        catch (err) {
                            instance.setState({
                                loading: false,
                                balance: '',
                                errorMessage: instance.translate.t('COINSALL_NOT_CLAIMED', true) + ': ' + err.message,
                            });
                        }
                    }
                }
            );
        }
    }

    handleChangeStep(value) {
        this.props.setStep(value);
    }

    changeDemo(value) {
        this.setState({isDemo: value});
    }

    render() {
        const instance = this,
            state = instance.state,
            props = instance.props,
            translate = instance.translate;
        return (
            <>
                <div className="coin-migration pure-u subpage">
                    <Title subTitle={translate.md('coin-migration')} title={translate.t('GEN2_COIN_MIGRATION')} />
                    <section className="step-wrapper">
                        <Step1
                            balance={state.balance}
                            completeStep1={props.completeStep1}
                            handleChangeStep={this.handleChangeStep}
                            handleDump={this.handleChangeWalletDump}
                            handlePrivate={this.handleChangePrivateKey}
                            hasBalance={!!state.balance && state.balance !== '0'}
                            loading={state.loading}
                            location={instance.props.location}
                            onChangeGen2Choice={this.handleChangeGen2Choice}
                            onChangeMnemonic={this.handleChangeMnemonic}
                            priv={state.privateKey}
                            ref={inst => instance._step1=inst}
                            step={props.step}
                            step1={props.step1}
                            translations={props.translations}
                            translationsMD={props.translationsMD}
                            walletType={state.gen2WalletType} />
                        <Step2
                            balance={state.balance}
                            completeStep2={props.completeStep2}
                            gen3Address={state.gen3Address}
                            handleChangeStep={this.handleChangeStep}
                            hasBalance={!!state.balance && state.balance !== '0'}
                            loading={state.loading}
                            onChangeGen3Address={this.handleChangeGen3Address}
                            step={props.step}
                            step2={props.step2}
                            translations={props.translations}
                            translationsMD={props.translationsMD} />
                        <Step3
                            balance={state.balance}
                            completeStep3={props.completeStep3}
                            gen3Address={state.gen3Address}
                            handleChangeStep={this.handleChangeStep}
                            handleMigrate={this.handleMigrate}
                            hasBalance={!!state.balance && state.balance !== '0'}
                            isClaimed={state.isClaimed}
                            loading={state.loading}
                            resetState={this.resetState}
                            step={props.step}
                            step3={props.step3}
                            translations={props.translations}
                            translationsMD={props.translationsMD} />
                    </section>
                    <Snackbar
                        action = {
                            <SnackbarAction
                                label={translate.t('CLOSE')}
                                onClick={() => instance.setState({errorMessage: ''})} />
                        }
                        message={state.errorMessage}
                        onClose={() => instance.setState({errorMessage: ''})}
                        open={!!state.errorMessage} />
                    <Footer {...instance.props} />
                </div>
            </>
        );
    }
}

export default Migrate;
